import React, { useState, useEffect } from 'react';

const AboutMission = () => {
  const [missionInfo, setMissionInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchMissionInfo();
  }, []);

  const fetchMissionInfo = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiUrl}/mission-info`);
      if (response.ok) {
        const data = await response.json();
        setMissionInfo(data);
      } else {
        setError('Failed to load mission information');
      }
    } catch (error) {
      console.error('Error fetching mission info:', error);
      setError('Failed to load mission information');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading mission information...</p>
        </div>
      </div>
    );
  }

  if (error || !missionInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{error || 'Mission information not available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {missionInfo.title || 'Our Mission'}
          </h1>
          {missionInfo.subtitle && (
            <p className="text-xl text-blue-600 font-semibold mb-4">
              {missionInfo.subtitle}
            </p>
          )}
          {missionInfo.description && (
            <p className="text-lg text-gray-700 leading-relaxed">
              {missionInfo.description}
            </p>
          )}
        </div>

        {/* Main Content Section */}
        {missionInfo.mainContent && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="prose max-w-none">
              {missionInfo.mainContent.split('\n').map((paragraph, index) => (
                paragraph.trim() && (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                )
              ))}
            </div>
          </div>
        )}

        {/* Trip Details Section */}
        {missionInfo.tripDetails && Object.keys(missionInfo.tripDetails).length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Trip Details</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {missionInfo.tripDetails.duration && (
                <div>
                  <h3 className="font-semibold text-blue-600 mb-2">Duration</h3>
                  <p className="text-gray-700">{missionInfo.tripDetails.duration}</p>
                </div>
              )}
              {missionInfo.tripDetails.location && (
                <div>
                  <h3 className="font-semibold text-blue-600 mb-2">Location</h3>
                  <p className="text-gray-700">{missionInfo.tripDetails.location}</p>
                </div>
              )}
              {missionInfo.tripDetails.dates && (
                <div>
                  <h3 className="font-semibold text-blue-600 mb-2">Dates</h3>
                  <p className="text-gray-700">{missionInfo.tripDetails.dates}</p>
                </div>
              )}
            </div>

            {missionInfo.tripDetails.activities && Array.isArray(missionInfo.tripDetails.activities) && missionInfo.tripDetails.activities.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-blue-600 mb-3">Activities</h3>
                <ul className="list-disc list-inside space-y-2">
                  {missionInfo.tripDetails.activities.map((activity, index) => (
                    <li key={index} className="text-gray-700">{activity}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Goals Section */}
        {missionInfo.goals && Array.isArray(missionInfo.goals) && missionInfo.goals.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Our Goals</h2>
            <ul className="space-y-3">
              {missionInfo.goals.map((goal, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-600 mr-3 text-xl">✓</span>
                  <span className="text-gray-700">{goal}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contact Information Section */}
        {missionInfo.contactInfo && Object.keys(missionInfo.contactInfo).length > 0 && (
          <div className="bg-blue-50 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Contact Us</h2>
            <div className="space-y-3">
              {missionInfo.contactInfo.email && (
                <p className="text-gray-700">
                  <span className="font-semibold">Email: </span>
                  <a href={`mailto:${missionInfo.contactInfo.email}`} className="text-blue-600 hover:underline">
                    {missionInfo.contactInfo.email}
                  </a>
                </p>
              )}
              {missionInfo.contactInfo.phone && (
                <p className="text-gray-700">
                  <span className="font-semibold">Phone: </span>
                  <a href={`tel:${missionInfo.contactInfo.phone}`} className="text-blue-600 hover:underline">
                    {missionInfo.contactInfo.phone}
                  </a>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-8 text-center">
          <a
            href="/"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            ← Back to Fundraisers
          </a>
        </div>
      </div>
    </div>
  );
};

export default AboutMission;
